import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, SkeletonText, Spinner } from '@livingdesign/react';
import { Close, CloudDownload, Search } from '@livingdesign/icons';
import Datatable from '../../../../../../common/Datatable/Datatable';
import Pagination from '../../../../../../common/Pagination/Pagination';
import TextInputField from '../../../../../../common/TextField/TextInputField';
import { useSavedFiltersManagement } from '../../../../../../../../contexts/SavedFiltersManagement';
import './DivisionDataTable.scss';
import { useRecipientFilter } from '../../../../../../../../contexts/RecipientFilter';
import { mapAttributeToLabel } from '../../../../../../../../utils/recipientUtils';

export const QA_ID_PREFIX = 'DivisionDataTable';

function DivisionDataTable({
    selectedFilter,
    qaPrefix = QA_ID_PREFIX,
    itemsPerPageDefault = 8, // Increased for better pagination with API
    className = '',
    setShowFilterEditModal,
    setShowRecipientDownloadModal,
    downloadInProgress,
    onDownload,
}) {
    const {
        divisionListData,
        isLoading,
        currentSearchTerm,
        hasMoreData,
        totalCount,
        isLoadingMore,
        searchRecipients,
        loadMoreRecipients,
        fetchRecipientsForFilter
    } = useSavedFiltersManagement();

    const {
        parseFilter,
    } = useRecipientFilter();

    // Local state for UI-only filtering and pagination
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageDefault);
    const [itemOffset, setItemOffset] = useState(0);
    const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
    
    // Ref to track if we need to load more data when user reaches end of current page
    const loadMoreTriggerRef = useRef(null);


    // Pagination calculations - working with filtered local data for display, but using totalCount for pagination
    const endOffset = itemOffset + parseInt(itemsPerPage);
    const currentContacts = filteredData.slice(itemOffset, endOffset);
    const totalItems = totalCount || filteredData.length; // Use totalCount from context if available

    // Initialize filtered data when divisionListData changes
    useEffect(() => {
        setFilteredData(divisionListData || []);
    }, [divisionListData]);

    // Reset state when selectedFilter changes
    useEffect(() => {
        if (selectedFilter) {
            setIsInitialLoadDone(false);
            setLocalSearchTerm('');
            setCurrentPage(0);
            setItemOffset(0);
        }
    }, [selectedFilter?.filterId, selectedFilter?.filter]);

    // Load initial data only once when component mounts and selectedFilter is available
    useEffect(() => {
        if (selectedFilter && !isInitialLoadDone && !isLoading) {
            console.log('DivisionDataTable: Loading initial data for filter:', selectedFilter.filterId);
            setIsInitialLoadDone(true);
            fetchRecipientsForFilter(selectedFilter, '', false);
        }
    }, [selectedFilter, isInitialLoadDone, isLoading, fetchRecipientsForFilter]);

    // Handle local search term changes and trigger API search with better conditions
    useEffect(() => {
        if (!selectedFilter || !isInitialLoadDone) return;

        // If search term matches current search term from context, no need to search again
        if (localSearchTerm === currentSearchTerm) return;

        console.log('DivisionDataTable: Search term changed:', { localSearchTerm, currentSearchTerm });

        if (localSearchTerm.trim()) {
            
            // Search via API with debouncing handled in context
            if (localSearchTerm.trim().length >= 3) // Only search if term is 3+ chars
                searchRecipients(selectedFilter, localSearchTerm.trim());
        } else if (localSearchTerm === '' && currentSearchTerm !== '') {
            // Clear search - fetch initial data only if we were previously searching
            fetchRecipientsForFilter(selectedFilter, '', false);
        }

        // Reset pagination when search changes (but not during load-more)
        if (!isLoadingMore) {
            setCurrentPage(0);
            setItemOffset(0);
        }
    }, [localSearchTerm, selectedFilter, currentSearchTerm, searchRecipients, fetchRecipientsForFilter, isInitialLoadDone, isLoadingMore]);

    // Check if we need to load more data when user reaches near the end of current data
    useEffect(() => {
        // Only check load more if we have initial data loaded and not currently loading
        if (!isInitialLoadDone || isLoading || !hasMoreData || isLoadingMore) return;
        const checkLoadMore = () => {   
            // Only load more if we have more data available and we're displaying all current data
            if (currentContacts.length > 0 && filteredData.length > 0) {
                const isNearEnd = (itemOffset + itemsPerPage) >= (filteredData.length - 5); // Load 5 items before end (reduced from 10)
                if (isNearEnd) {
                    loadMoreRecipients(selectedFilter, localSearchTerm.trim());
                }
            }
        };

        // Debounce the check to prevent rapid firing
        const debounceTimer = setTimeout(checkLoadMore, 100);
        return () => clearTimeout(debounceTimer);
    }, [itemOffset, itemsPerPage, filteredData.length, hasMoreData, isLoading, currentContacts.length, loadMoreRecipients, selectedFilter, localSearchTerm, isInitialLoadDone, currentPage, totalCount, isLoadingMore]);

    // Effect to restore focus when search input becomes enabled after search completes
   // Effect to restore focus when search input becomes enabled after search completes
    // useEffect(() => {
    //     if (isInitialLoadDone && !isLoading && !isLoadingMore)  {
    //         // Small delay to ensure the input is fully enabled
    //         console.log('inside restore focus useEffect', { isLoading, isLoadingMore });
    //         setTimeout(() => {
    //             // Find the search input by its name attribute and focus it
    //             console.log('inside settimeout restore focus useEffect', { isLoading, isLoadingMore });
    //             const searchInput = document.querySelector('div[name="division-search-TextField"] input');
    //             if (searchInput) {
    //                 searchInput.focus();
    //             }
    //         }, 50);
    //     }
    // }, [isInitialLoadDone, isLoading, isLoadingMore]);

    const handleClearSearch = () => {
        setLocalSearchTerm('');
    };


    // Row rendering helper functions
    const renderContactNameCell = (contact) => (
        <div className="contact-cell-content" data-testid={`${qaPrefix}-contact-cell-content`}>
            <div className="contact-name" data-testid={`${qaPrefix}-contact-name`}>{contact.name}</div>
            <div className="contact-description" data-testid={`${qaPrefix}-contact-description`}>{contact.description}</div>
        </div>
    );

    const renderFilters = useCallback((parsedRawFilter) => {
        const filters = [];
        let index = 0;
        while (index <= parsedRawFilter.length) {
            const attribute = parsedRawFilter[index]?.attribute;
            const values = parsedRawFilter[index]?.values;
            let operator;
            if (index < parsedRawFilter.length) {
                operator = parsedRawFilter[index + 1]?.value;
            }
            filters.push({
                attribute,
                values,
                operator,
            });
            index += 2;
        }
        console.log('Rendered filters:', filters);
        return filters;
    }, [] );

    const parsedFilterForTableHeader = useMemo(() => {
       if (!selectedFilter) return null;
       const parsedRawFilter = parseFilter(selectedFilter?.filter);
       const filtersArray = renderFilters(parsedRawFilter);
        return (
            <div className="applied-filters-container" data-testid={`${qaPrefix}-applied-filters-container`}>
                {filtersArray?.map((filter, index) => (
                    <div key={index} className="filter-group" data-testid={`${qaPrefix}-filter-group-${index}`}>
                        {filter.attribute && filter.values && filter.values.length > 0 && (
                            <>
                                <span className="filter-label" data-testid={`${qaPrefix}-filter-label-${index}`}>{mapAttributeToLabel(filter.attribute)}:</span>
                                <div className="filter-tags" data-testid={`${qaPrefix}-filter-tags-${index}`}>
                                    {filter.values.map((value, valIndex) => (
                                        <span
                                            className="filter-value-tag"
                                            data-testid={`${qaPrefix}-filter-value-tag-${index}-${valIndex}`}
                                        >
                                            {value}
                                        </span>
                                    ))}
                                    {filter.operator && (
                                        <span
                                            className="filter-operator-tag"
                                            data-testid={`${qaPrefix}-filter-operator-tag-${index}`}
                                        >
                                            {filter.operator}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        );
    }, [selectedFilter, parseFilter, renderFilters]);

    const renderEmailCell = (contact) => (
        <div className="email-cell" data-testid={`${qaPrefix}-email-cell`}>
            <span className="contact-email" data-testid={`${qaPrefix}-contact-email`}>{contact.email}</span>
        </div>
    );

    // Create table row for a contact
    const createTableRow = (contact) => [
        renderContactNameCell(contact),
        renderEmailCell(contact)
    ];

    // Table preparation - convert contacts to table rows
    const tableHeaders = [
        { label: 'Contact name', key: 'name' },
        { label: 'Email', key: 'email' },
    ];

    const tableRows = currentContacts.map(createTableRow);

    // Handle load more manually if needed
    const handleLoadMore = () => {
        if (hasMoreData && !isLoading) {
            loadMoreRecipients(selectedFilter, localSearchTerm.trim());
        }
    };

    return (
        <>
                <div className={`division-data-table ${className}`} data-testid={`${qaPrefix}-root`}>
                    {/* Search Field */}
                    <div className="division-search-section" data-testid={`${qaPrefix}-search-section`}>
                        <div className="division-search-container" data-testid={`${qaPrefix}-search-container`}>
                            <TextInputField
                                label=""
                                textFieldProps={{
                                    placeholder: "Search contacts"
                                }}
                                value={localSearchTerm}
                                handleChange={setLocalSearchTerm}
                                field={{
                                    label: "",
                                    disabled: false,
                                    leadingIcon: <Search />,
                                    trailingIcon: localSearchTerm ? <Close data-testid={`${qaPrefix}-clear-search-button`} onClick={handleClearSearch}/> : undefined,
                                    trailingIconLabel: "Clear search",
                                    inputProps: {
                                        placeholder: "Search contacts",
                                    },
                                    className: "division-search-field",
                                    size:"large"
                                }}
                                id="division-search"
                                componentKey="division-search-field"
                            />
                        </div>
                        {/* ::: Code Generated by Copilot 2f7b0e3b-2a1a-4b0c-9c9f-7f5e6a9d1c2b. This comment will be removed automatically after the file is saved ::: */}
                        <div className="division-actions-container" data-testid={`${qaPrefix}-actions-container`}>
                            <Button
                                variant="tertiary"
                                leading={<CloudDownload />}
                                disabled={isLoading || downloadInProgress || (divisionListData?.length === 0)}
                                size="medium"
                                onClick={onDownload}
                                data-testid={`${qaPrefix}-download-button`}
                            >
                                {downloadInProgress && <Spinner size='small' />}
                                Download
                            </Button>
                            <Button
                                variant="primary"
                                disabled={isLoading}
                                size="medium"
                                onClick={() => { setShowFilterEditModal(true);}}
                                data-testid={`${qaPrefix}-edit-button`}
                            >
                                Edit Filters
                            </Button>
                        </div>
                    </div>

                    <div className="division-applied-filters" data-testid={`${qaPrefix}-applied-filters`}>
                        <h3 className="applied-filters-title" data-testid={`${qaPrefix}-applied-filters-title`}>Applied filters:</h3>
                        {parsedFilterForTableHeader}
                    </div>
                    
                    {/* Always render the container to maintain consistent width */}
                    <div className="division-table-container" data-testid={`${qaPrefix}-table-container`}>
                        {/* Show skeleton only during initial loading, not during load-more */}
                        { isLoading && !isLoadingMore ? 
                            (
                                <div className="division-loading-skeleton" style={{ width: '100%' }} data-testid={`${qaPrefix}-loading-skeleton`}>
                                    <SkeletonText lines={6}/>
                                </div>
                            ) : 
                            (
                                <Datatable
                                    headers={tableHeaders}
                                    rows={tableRows}
                                    emptyRowsMessage={
                                        divisionListData?.length === 0 ? "No contacts found" :
                                        filteredData.length === 0 && divisionListData?.length > 0 ? `No contacts found matching "${localSearchTerm}"` :
                                        isLoading && !isLoadingMore ? "Loading contacts..." :
                                        null
                                    }
                                    data-testid={`${qaPrefix}-table`}
                                />
                            )
                        }

                        {/* Loading indicator for load-more operations */}
                        {isLoadingMore && (
                            <div className="division-loading-more" style={{textAlign: 'center', margin: '16px 0', width: '100%'}} data-testid={`${qaPrefix}-loading-more`}>
                                <SkeletonText lines={2} />
                            </div>
                        )}

                        {/* Pagination - Updated for better integration with API pagination */}
                        {totalItems > 0 && !isLoading && (
                            <div className="division-pagination" data-testid={`${qaPrefix}-pagination`}>
                                <Pagination
                                    totalItems={totalItems}
                                    itemsPerPageOptions={[8, 16, 24]}
                                    itemsPerPage={itemsPerPage}
                                    setItemsPerPage={setItemsPerPage}
                                    itemOffset={itemOffset}
                                    setItemOffset={setItemOffset}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                    
                </div>
        </>
    );
}

export default DivisionDataTable;
